﻿import { NodeType, NodeSegmentVariation, NodeConditionVariation, NodeTaskVariation } from '@journeys/Enums';
import { JourneyNode, JourneyLine } from '@journeys/Journeys.viewmodel';

export function validateConnectionType(fromNode: JourneyNode, toNode: JourneyNode, nodeList: JourneyNode[], lines: JourneyLine[]): boolean {
    // Prevent direct connection from Condition node to Segment node
    if (fromNode.data.type === NodeType.Condition && toNode.data.type === NodeType.Segment && toNode.data.var === NodeSegmentVariation.Segment) {
        return false;
    }

    const excludeNodes = getNodesInLine(fromNode, nodeList, lines);
    const fromNodeContainsActionNode = lineIncludesActionNodeBackwards(fromNode, nodeList, lines, excludeNodes) || isActionNode(fromNode);
    const toNodeContainsActionNode = lineIncludesActionNodeBackwards(toNode, nodeList, lines, excludeNodes);

    // Handle cases where both nodes contain action nodes
    if (fromNodeContainsActionNode && toNodeContainsActionNode) {
        // Allow connection if both nodes contain the same action node
        if (fromNodeContainsActionNode === toNodeContainsActionNode) {
            return true;
        }

        // Only allow connection to Delay condition if not connecting to the same action node
        if (toNode.data.type !== NodeType.Condition && toNode.data.var !== NodeConditionVariation.Delay) {
            return false;
        }
    }

    // Allow connection from Email/SMS task to Delay condition
    if (toNode.data.type === NodeType.Condition && toNode.data.var === NodeConditionVariation.Delay) {
        if (fromNode.data.type === NodeType.Task && (fromNode.data.var === NodeTaskVariation.Email || fromNode.data.var === NodeTaskVariation.Sms)) {
            return true;
        }
    }

    // Allow any connection from a Condition node
    if (fromNode.data.type === NodeType.Condition) {
        return true;
    }

    // Handle connections from Segment nodes
    if (fromNode.data.type === NodeType.Segment && fromNode.data.var === NodeSegmentVariation.Segment) {
        // Allow connection to action nodes (Email/SMS)
        if (isActionNode(toNode)) {
            return true;
        }
        // Allow connection to Condition nodes
        if (toNode.data.type === NodeType.Condition && toNode.data.var === NodeConditionVariation.Condition) {
            return true;
        }
    }

    // Allow connections from action nodes to Condition nodes
    if (isActionNode(fromNode)) {
        if (toNode.data.type === NodeType.Condition) {
            return true;
        }
    }

    // Handle connections from Event/Survey tasks
    if (fromNode.data.type === NodeType.Task && (fromNode.data.var === NodeTaskVariation.Event || fromNode.data.var === NodeTaskVariation.Survey)) {
        // Allow connection to specific condition types related to events
        if (
            toNode.data.type === NodeType.Condition &&
            (toNode.data.var === NodeConditionVariation.Condition ||
                toNode.data.var === NodeConditionVariation.EventAccepted ||
                toNode.data.var === NodeConditionVariation.EventCancelled ||
                toNode.data.var === NodeConditionVariation.EventDeclined ||
                toNode.data.var === NodeConditionVariation.EventInvited ||
                toNode.data.var === NodeConditionVariation.EventNoShow ||
                toNode.data.var === NodeConditionVariation.EventRegistered ||
                toNode.data.var === NodeConditionVariation.EventWaitlist)
        ) {
            return true;
        } else return false;
    }

    // Allow connections from action nodes to Event/Survey tasks
    if (
        isActionNode(fromNode) &&
        ((toNode.data.type === NodeType.Task && toNode.data.var === NodeTaskVariation.Event) || toNode.data.var === NodeTaskVariation.Survey)
    ) {
        return true;
    }

    return false;
}

export function lineIncludesActionNodeBackwards(
    node: JourneyNode,
    nodeList: JourneyNode[],
    lines: JourneyLine[],
    excludeNodes: Set<number>
): JourneyNode | null {
    const linesBackwards = lines.filter((line) => line.to === node.id);

    for (const line of linesBackwards) {
        const previousNode = nodeList.find((n) => n.id === line.fr);

        // Check if previous node exists and is not in excluded nodes
        if (previousNode && !excludeNodes.has(previousNode.id)) {
            // Return if previous node is an action node
            if (isActionNode(previousNode)) {
                return previousNode;
            }
            // Recursively check for action nodes further back in the line
            const actionNode = lineIncludesActionNodeBackwards(previousNode, nodeList, lines, excludeNodes);
            if (actionNode) {
                return actionNode;
            }
        }
    }

    return null;
}

function getNodesInLine(startNode: JourneyNode, nodeList: JourneyNode[], lines: JourneyLine[]): Set<number> {
    const excludeNodes = new Set<number>();
    let currentNode = startNode;

    while (currentNode) {
        excludeNodes.add(currentNode.id);
        const lineForward = lines.find((line) => line.fr === currentNode.id);
        // Continue traversing if there's a next node in the line
        if (lineForward) {
            const nextNode = nodeList.find((n) => n.id === lineForward.to);
            if (nextNode) {
                currentNode = nextNode;
                continue;
            }
        }
        break;
    }

    return excludeNodes;
}

function isActionNode(node: JourneyNode) {
    // Check if node is an Email or SMS task type
    if (node.data.type === NodeType.Task && (node.data.var === NodeTaskVariation.Email || node.data.var === NodeTaskVariation.Sms)) {
        return true;
    }
    return false;
}
